import { trayUuidFromRouteSlug } from '@/js/core/utils/tray-route';
import { sameOriginUrl } from '@/js/core/utils/return-path';
import { scrollToTopOnReturn } from '@/js/core/utils/return-scroll';
import { cancelTrayPrefetch, scheduleTrayPrefetch } from '@/js/core/utils/tray-prefetch';

// An explicit opt-in in the markup rather than a style class: a block hands its links
// to the router by adding the attribute, and a CSS rename cannot silently break routing.
const CARD_SELECTOR = '[data-spa-link]';

let hoveredCard = null;
let spaRouter = null;

/**
 * Route opted-in block links through the SPA instead of a document load, and warm the
 * tray payload while the pointer rests on a card.
 *
 * The links are server-rendered inside Gutenberg content, so this delegates from the
 * document rather than binding per link — content swaps need no re-init.
 */
export function initFeaturedTrayLinks(router) {
  if (spaRouter) return;

  spaRouter = router;

  document.addEventListener('pointerover', handlePointerOver);
  document.addEventListener('focusin', handleFocusIn);
  document.addEventListener('click', handleClick);
}

function handlePointerOver(event) {
  const card = cardFromEvent(event);

  // pointerover repeats for every child crossed inside the same card; only a change
  // of card is a new hover.
  if (card === hoveredCard) return;

  hoveredCard = card;

  if (!card) {
    cancelTrayPrefetch();
    return;
  }

  scheduleTrayPrefetch(trayRoute(card)?.uuid);
}

// Tabbing through a grid sweeps focus the same way a pointer sweeps it, so this waits
// on the same dwell rather than warming every card it passes.
function handleFocusIn(event) {
  const card = cardFromEvent(event);

  if (card) scheduleTrayPrefetch(trayRoute(card)?.uuid);
}

function handleClick(event) {
  const card = cardFromEvent(event);

  if (!card) return;

  // Let Cmd/Ctrl/Shift/Alt-click open in a new tab/window natively.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const route = trayRoute(card);

  if (!route) return;

  event.preventDefault();
  scrollToTopOnReturn();
  spaRouter.push(route.target);
}

function cardFromEvent(event) {
  return event.target?.closest?.(CARD_SELECTOR) || null;
}

/**
 * Resolve the card's href to the tray detail route.
 *
 * The router parses the href, so the query survives intact — the `return` param is
 * what sends the modal back to the page the block sits on — and the uuid comes from
 * the resolved params, leaving the tray URL shape owned by the route table.
 */
function trayRoute(card) {
  const href = card.getAttribute('href');
  const url = href ? sameOriginUrl(href) : null;

  if (!url) return null;

  const resolved = spaRouter.resolve(`${url.pathname}${url.search}`);

  if (resolved.name !== 'tray-view') return null;

  return { target: resolved, uuid: trayUuidFromRouteSlug(resolved.params.traySlug) };
}
